import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import 'moment-timezone';
import {
  Typography,
  Grid,
  IconButton,
  Badge,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import LetteredAvatar from 'react-lettered-avatar';
import PushPinIcon from '@mui/icons-material/PushPin';
import { orange } from '@material-ui/core/colors';
import * as noteActions from '../../../redux/actions/notes';
import { isNotificationUnread } from '../../../helpers/pricing';
import { convertNotesDateToPST } from '../../../helpers/audit-trail';
import { updatePin } from '../../../helpers/api';
import './react-lettered.css';

const styles = makeStyles(() => ({
  noteCardContent: {
    padding: '5px',
    position: 'relative',
  },
  noteCardUser: {
    fontSize: '14px',
  },
  noteCardText: {
    fontSize: '14px',
    wordWrap: 'break-word',
  },
  noteCardDate: {
    fontSize: '12px',
  },
  noteCardNew: {
    color: orange[500],
  },
  fadeOut: {
    opacity: 0,
    width: 0,
    height: 0,
    transition: 'width 2s 2s, height 2s 2s, opacity 2s',
  },
  fadeIn: {
    opacity: 1,
    transition: 'width 0.5s, height 0.5s, opacity 0.5s 0.5s',
  },
  listNoteMd: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 240,
  },
  listNote: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 370,
  },
  listNoteLg: {
    borderRadius: '5px !important',
    padding: '8px !important',
    paddingTop: '0px !important',
    margin: '0px !important',
    width: 330,
  },
  noteDetail: {
    backgroundColor: '#EEEEEE !important',
  },
  noteDetailDark: {
    backgroundColor: '#2D3436  !important',
  },
  pinnedNote: {
    backgroundColor: 'transparent  !important',
  },
  pinnedItem: {
    backgroundColor: '#FEF9EC  !important',
  },
  customBadge: {
    backgroundColor: orange[500],
    height: 12,
    width: 12,
    borderRadius: 10,
  },
  pinnedPin: {
    color: orange[500],
    visibility: 'visible',
    transform: 'rotate3d(1, 6, 18, 50deg)',
    '&:hover': {
      color: orange[500],
      backgroundColor: 'rgba(232, 145, 58, 0.1)',
      transform: 'rotate3d(1, 6, 18, 40deg)',
    },
  },
  unpinnedPin: {
    color: 'gray',
    visibility: 'visible',
    '&:hover': {
      color: '#ab47bc',
      backgroundColor: 'rgba(171, 71, 188, 0.1)',
    },
  },
  hiddenPin: {
    visibility: 'hidden',
  },
  listItem: {
    alignItems: 'flex-end',
    marginBottom: 5,
    width: '96%',
  },
  darkNoteName: {
    color: '#2D3436',
  },
  darkNoteText: {
    color: '#707171',
  },
}));

const Note = props => {
  const theme = useTheme();
  const { note, newNotes, handleNoteClick, handlePin } = props;
  const noteDate = convertNotesDateToPST(note.created_at).replace(',', '');
  const [isUnread, setUnread] = useState(false);
  const [isPinned, setPinned] = useState(false);
  const [inFocus, setFocus] = useState(false);
  const pageMD = useMediaQuery(theme.breakpoints.only('md'));
  const pageLG = useMediaQuery(theme.breakpoints.only('lg'));
  const pageXL = useMediaQuery(theme.breakpoints.only('xl'));

  const classes = styles();
  const getUserStringBase = () => {
    const { user_id: userID, display_name: displayName, user_email: userEmail } = note;
    let userText = userID;
    if (typeof displayName !== 'undefined' && displayName.length) userText = displayName;
    else if (typeof userEmail !== 'undefined' && userEmail.length) userText = userEmail;

    let words = userText.split(' ');
    if (words && words.length > 0) {
      words = words.filter(word => word.length > 0);
      return words
        .map(word => {
          return word[0].toUpperCase() + word.substring(1);
        })
        .join(' ');
    }

    return userText;
  };

  useEffect(() => {
    if (newNotes) {
      const unread = isNotificationUnread(note, newNotes);
      setUnread(unread);
    }
  }, [newNotes]);

  function splitByUrl(text) {
    const regex = /(https?:\/\/[^\s]+)|(\S+)/g;
    const textParts = [];
    let match;
    /* eslint-disable-next-line no-cond-assign */
    while ((match = regex.exec(text)) !== null) {
      const [matchCompleto, url] = match;
      if (url) {
        textParts.push(url);
      } else {
        switch (textParts) {
          case textParts.length > 0 && !textParts[textParts.length - 1].includes('http'):
            /* eslint-disable-next-line prefer-template */
            textParts[textParts.length - 1] += ' ' + matchCompleto;
            break;
          case textParts.length > 0 && textParts[textParts.length - 1].includes('http'):
            textParts.push(matchCompleto);
            break;
          case textParts.length === 0:
            textParts.push(matchCompleto);
            break;
          default:
            textParts.push(matchCompleto);
        }
      }
    }
    return textParts;
  }

  function textWithHiperlink(noteText) {
    const dividedText = splitByUrl(noteText);
    if (dividedText && dividedText.length > 0) {
      return (
        <div>
          {dividedText.map(text => (
            <span>
              {text.includes('http') ? (
                <a
                  className={classes.noteCardText}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={text}
                  style={{ color: theme.palette.type === 'light' ? '#9c27b0' : '#ba68c8' }}
                >
                  {text}
                  {` `}
                </a>
              ) : (
                <span className={classes.noteCardText}>
                  {text}
                  {` `}
                </span>
              )}
            </span>
          ))}
        </div>
      );
    }
    return <span>{noteText}</span>;
  }

  function returnNoteContent(theNote) {
    const contentNote = theNote.content;
    if (contentNote.includes('\n')) {
      return (
        <div>
          {contentNote.split('\n').map((line, index) => (
            <div>
              {line.includes('http') ? (
                <div>
                  <p
                    className={classes.noteCardText}
                    style={{
                      marginBottom: index === contentNote.split('\n').length - 1 ? '0px' : '-14px',
                      marginTop: index === 0 && '-5px',
                      wordWrap: 'break-word',
                    }}
                  >
                    {textWithHiperlink(line)}
                  </p>
                </div>
              ) : (
                <div>
                  <p
                    className={classes.noteCardText}
                    style={{
                      marginBottom: index === contentNote.split('\n').length - 1 ? '0px' : '-14px',
                      marginTop: index === 0 && '-5px',
                      wordWrap: 'break-word',
                      wordBreak: 'normal',
                    }}
                  >
                    {line}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div>
        {contentNote.includes('http') ? (
          <div>{textWithHiperlink(contentNote)}</div>
        ) : (
          <span className={classes.noteCardText}>{contentNote}</span>
        )}
      </div>
    );
  }

  useEffect(() => {
    if (note.pinned) {
      setPinned(true);
    } else {
      setPinned(false);
    }
  }, [note]);

  const handlePinned = () => {
    const pinned = !isPinned;
    setPinned(pinned);
    updatePin({
      entityID: note.entity,
      userVersion: note.user_version,
      pinned,
    });
    handlePin(note, pinned);
  };

  const handleFocus = (event, focus) => {
    setFocus(focus);
  };

  const noteHeader = (
    <Grid container direction="row" alignItems="baseline" justifyContent="space-between">
      <Grid item>
        <Grid container direction={pageMD ? 'column' : 'row'} alignItems="baseline" spacing={pageMD ? 0 : 1}>
          <Grid item>
            <Typography className={theme.palette.type === 'dark' && isPinned ? classes.darkNoteName : ''}>
              <span className={classes.noteCardUser}>{getUserStringBase()}</span>
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              className={`${classes.noteCardDate} ${
                theme.palette.type === 'dark' && isPinned ? classes.darkNoteText : ''
              }`}
            >
              {noteDate.includes('PM') ? noteDate.replace('PM', 'P.M.') : noteDate.replace('AM', 'A.M.')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <IconButton
          aria-label="pin"
          /* eslint-disable-next-line no-nested-ternary */
          className={isPinned ? classes.pinnedPin : inFocus ? classes.unpinnedPin : classes.hiddenPin}
          onClick={handlePinned}
        >
          <PushPinIcon />
        </IconButton>
      </Grid>
    </Grid>
  );

  return (
    <ListItem
      onMouseEnter={event => handleFocus(event, true)}
      onMouseLeave={event => handleFocus(event, false)}
      onClick={() => handleNoteClick(note)}
      className={`${classes.listItem} ${isPinned ? classes.pinnedItem : ''}`}
    >
      <ListItemAvatar style={{ minWidth: 40 }}>
        <LetteredAvatar size={35} style={{ fontSize: '16px', color: 'white' }} name={getUserStringBase()} />
      </ListItemAvatar>
      <Badge variant="dot" size="large" classes={{ badge: classes.customBadge }} invisible={!isUnread}>
        <ListItemText
          primary={noteHeader}
          secondary={returnNoteContent(note)}
          className={`${
            /* eslint-disable-next-line no-nested-ternary */
            pageXL ? classes.listNote : pageLG ? classes.listNoteLg : classes.listNoteMd
          }
          ${
            /* eslint-disable-next-line no-nested-ternary */
            isPinned ? classes.pinnedNote : theme.palette.type === 'light' ? classes.noteDetail : classes.noteDetailDark
          }`}
          secondaryTypographyProps={{
            style: {
              marginTop: pageMD ? 2 : -10,
              color:
                /* eslint-disable-next-line no-nested-ternary */
                theme.palette.type === 'dark' && isPinned
                  ? '#707171'
                  : theme.palette.type === 'dark'
                  ? 'white'
                  : 'black',
            },
          }}
        />
      </Badge>
    </ListItem>
  );
};
Note.propTypes = {
  handleNoteClick: PropTypes.func.isRequired,
  handlePin: PropTypes.func.isRequired,
  newNotes: PropTypes.array.isRequired,
  note: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  noteEditError: state.notes.ui.noteEditError,
});

const mapDispatchToProps = dispatch => ({ eventActions: bindActionCreators(noteActions, dispatch) });

export default connect(mapStateToProps, mapDispatchToProps)(Note);
